/**
 * useAllowedAirlines
 * Which airlines' flights the display boards show. Managed on the admin
 * "Airlines" page and served by GET /api/airlines/allowed.
 *
 * Never leaves a board without a list: starts from the last list received
 * (localStorage) or the built-in default, and keeps what it has whenever the
 * server is unreachable or returns something unusable.
 *
 * The same response carries the logo versions for airline logos stored on the
 * admin page; they are handed to the airline logo registry.
 */

import { useEffect, useState } from 'react';
import { safeGet } from '../config/axiosClient.js';
import { apiConfigReady } from '../config/config.js';
import { setLogoVersions } from '../services/airlineLogoRegistry.js';

const STORAGE_KEY = 'fids_allowed_airlines';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export const DEFAULT_ALLOWED_AIRLINES = [
  "P4","TK","ET","AF","KL","LH","BA","VS","DL","UA","MS","QR","AT","WB","SA","ME",
  "UR","HF","KP","DT","AW","VK","J7","TC","EK","X9","NG","SQ"
];

const normalizeCodes = (list) => {
  if (!Array.isArray(list)) return null;
  const codes = [...new Set(
    list
      .map(code => String(code).trim().toUpperCase())
      .filter(code => /^[A-Z0-9]{2,3}$/.test(code))
  )];
  return codes.length > 0 ? codes : null;
};

const readCachedCodes = () => {
  try {
    return normalizeCodes(JSON.parse(localStorage.getItem(STORAGE_KEY)));
  } catch (err) {
    return null;
  }
};

const sameCodes = (a, b) => a.length === b.length && a.every((code, i) => code === b[i]);

export function useAllowedAirlines() {
  const [airlines, setAirlines] = useState(() => readCachedCodes() || DEFAULT_ALLOWED_AIRLINES);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      const { data, error } = await safeGet('/api/airlines/allowed');
      if (cancelled || error || !data?.success) return; // keep current list

      setLogoVersions(data.logos); // ignored when missing or empty

      const codes = normalizeCodes(data.data);
      if (!codes) return; // never replace a working list with an empty one

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
      } catch (err) {
        // Storage unavailable - the in-memory list still applies
      }
      setAirlines(prev => (sameCodes(prev, codes) ? prev : codes));
    };

    apiConfigReady.then(() => { if (!cancelled) refresh(); });
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return airlines;
}
